/* eslint-disable react/no-did-update-set-state */
/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react'
import { node } from 'prop-types'
import axios from 'axios'
import debounce from 'lodash/debounce'
import Promise from 'bluebird'

import { trackEvent } from 'utils/tracking'
import {
  getCheckout,
  createCheckoutMutation,
  checkoutLineItemsAdd,
  checkoutLineItemsUpdate,
  checkoutLineItemsRemove,
  checkoutDiscountCodeApply,
  checkoutDiscountCodeRemove
} from 'queries/checkout'
import Toast from 'components/Toast'
import { TOAST_DURATION_MS } from 'utils/constants'
import useDigitalData from 'hooks/useDigitalData'
import usePrevious from 'hooks/usePrevious'

const Context = React.createContext({
  loading: true
})

const CheckoutProvider = ({ children }) => {
  const [checkout, setCheckout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [checkoutUpToDate, setCheckoutUpToDate] = useState(true)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [discount, setDiscount] = useState(null)
  const [authSdk, setAuthSdk] = useState(null)
  const [user, setUser] = useState(null)
  const [priceBreakdown, setPriceBreakdown] = useState(null)
  const [priceBreakdownLoading, setPriceBreakdownLoading] = useState(false)
  const [tinyCartVisible, setTinyCartVisible] = useState(false)
  const [regularLineItems, setRegularLineItems] = useState(null)
  const [subscriptionLineItems, setSubscriptionLineItems] = useState(null)
  const [regularLineItemsQuantity, setRegularLineItemsQuantity] = useState(null)
  const [subscriptionLineItemsQuantity, setSubscriptionLineItemsQuantity] = useState(null)
  const [applyDiscountCodeError, setApplyDiscountCodeError] = useState(null)
  const [products, setProducts] = useState([])
  const [draftOrder, setDraftOrder] = useState({})
  const [skipSubscription, setSkipsubscription] = useState(false)
  const [queuedOrder, setQueuedOrder] = useState(null)

  const { digitalData } = useDigitalData()
  const prevCheckout = usePrevious(checkout)

  console.log(subscriptionLineItems)

  useEffect(() => {
    try {
      if (!checkout || !regularLineItems || !subscriptionLineItems) return

      const discount = priceBreakdown?.regular?.discount

      const combinedLineItems = [
        ...subscriptionLineItems.map(li => ({ ...li, isSubscription: true })),
        ...regularLineItems.map(li => ({ ...li, isSubscription: false }))
      ]

      const subtotal = (
        parseFloat(priceBreakdown?.regular?.lineItemsSubtotalPrice || '0.00') +
        parseFloat(priceBreakdown?.subscription?.lineItemsSubtotalPrice || '0.00')
      ).toFixed(2)

      const total = (
        parseFloat(priceBreakdown?.regular?.total || '0.00') + parseFloat(priceBreakdown?.subscription?.total || '0.00')
      ).toFixed(2)

      digitalData.push({
        cart: {
          cartId: checkout.id,
          price: {
            subtotal,
            cartTotal: total,
            coupon: discount
              ? {
                  couponCode: discount.code,
                  couponDiscount: discount.value
                }
              : null
          },
          productInfo: combinedLineItems.map(({ node: li, isSubscription }) => ({
            productId: li.variant.id,
            productName: li.title,
            price: li.variant.price,
            quantity: li.quantity,
            attributes: {
              orderType: isSubscription ? 'Subscription' : 'One-Time',
              ...li.variant.selectedOptions.reduce(
                (acc, { name, value }) => ({ ...acc, [name.toLowerCase()]: value }),
                {}
              )
            }
          }))
        }
      })

      window._satellite?.track('checkout-step')
    } catch (e) {
      console.log(e)
    }
  }, [regularLineItems, subscriptionLineItems, priceBreakdown])

  useEffect(() => {
    if (!process.browser) return

    const localStorageCheckoutId = localStorage.getItem('checkoutId')

    if (localStorageCheckoutId) {
      getCurrentCart(localStorageCheckoutId)
    } else {
      if (!checkout) {
        createCheckout()
      }
    }
  }, [])

  useEffect(() => {
    if (authSdk) {
      fetchProducts()
    }
  }, [authSdk])

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search.slice(1))
    const queryParams = Array.from(qs.entries()).reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
    const discount = queryParams.discount

    setDiscount(discount)
  }, [discount])

  useEffect(() => {
    if (authSdk) {
      dispatchPriceBreakdown()
      splitCheckoutLineItems()
      dispatchDraftOrder()
    }
  }, [checkout])

  useEffect(() => {
    if (authSdk && checkout) {
      dispatchPriceBreakdown()
    }
  }, [user])

  useEffect(() => {
    if (authSdk) {
      dispatchPriceBreakdown()
    }
  }, [skipSubscription])

  const fetchProducts = async () => {
    const { products } = await authSdk.getProducts()
    setProducts(products)
  }

  const dispatchDraftOrder = () => {
    // console.log('dispatchDraftOrder')

    if (checkout) {
      const subscriptionLineItems = checkout.lineItems.edges.filter(item =>
        item.node.customAttributes.find(a => a.key === 'orderType' && a.value === 'subscription')
      )

      // console.log(subscriptionLineItems)

      setDraftOrder({
        ...draftOrder,
        [checkout.id]: subscriptionLineItems
      })
    }
  }

  const splitCheckoutLineItems = () => {
    if (checkout) {
      const { edges } = checkout.lineItems

      const subscriptionLineItems = edges.filter(p =>
        p.node.customAttributes.find(a => a.key === 'orderType' && a.value === 'subscription')
      )

      const regularLineItems = edges.filter(p => !p.node.customAttributes.find(a => a.key === 'orderType'))

      const regularLineItemsQuantity = regularLineItems
        .map(p => p.node.quantity)
        .reduce((total, currentValue) => total + currentValue, 0)

      const subscriptionLineItemsQuantity = subscriptionLineItems
        .map(p => p.node.quantity)
        .reduce((total, currentValue) => total + currentValue, 0)

      setSubscriptionLineItems(subscriptionLineItems)
      setRegularLineItems(regularLineItems)
      setRegularLineItemsQuantity(regularLineItemsQuantity)
      setSubscriptionLineItemsQuantity(subscriptionLineItemsQuantity)
    }
  }

  const setCheckoutUser = user => {
    setUser(user)
  }

  const dispatchPriceBreakdown = async (shippingMethodPrice = null) => {
    setPriceBreakdownLoading(true)

    const body = {
      checkout,
      tierLevel: user?.currentTierLevel,
      province: user?.province,
      shippingMethodPrice,
      skipSubscription
    }

    const { priceBreakdown } = await authSdk.getPriceBreakdown({
      params: body,
      token: localStorage.token || null
    })

    setPriceBreakdown(priceBreakdown)
    setPriceBreakdownLoading(false)
  }

  const createCheckout = async ({ customAttributes = {} } = {}, currentCart = true) => {
    let input = {}
    const localStorageToken = process.browser ? localStorage.getItem('token') : null

    if (localStorageToken) {
      input = {
        ...input,
        customAttributes: [
          {
            key: 'jwt',
            value: localStorageToken
          },
          ...Object.entries(customAttributes).map(([key, value]) => ({
            key,
            value
          }))
        ]
      }
    }

    const responseCheckout = await createCheckoutMutation({ input })
    setCheckout(responseCheckout)
    if (process.browser && currentCart) localStorage.setItem('checkoutId', responseCheckout.id)

    window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
      quantity: e.node.quantity,
      variantId: e.node.variant.id,
      price: e.node.variant.price,
      name: e.node.title
    }))
    setLoading(false)
  }

  const getCurrentCart = async checkoutId => {
    setLoading(true)

    try {
      const responseCheckout = await getCheckout({ checkoutId })

      window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
        quantity: e.node.quantity,
        variantId: e.node.variant.id,
        price: e.node.variant.price,
        name: e.node.title
      }))

      if (!responseCheckout || responseCheckout.lineItems.edges.filter(item => item.node.variant === null).length) {
        createCheckout()
      } else {
        setCheckout(responseCheckout)
        setLoading(loading)
      }
    } catch (e) {
      localStorage.removeItem('checkoutId')
      createCheckout()
    }

    setLoading(false)
  }

  const addLineItemsToCart = async (lineItems, { silent } = { silent: false }) => {
    setLoading(true)

    const formattedLineItems = lineItems.map(l => {
      const customAttributes = l.customAttributes || []

      return {
        quantity: l.quantity,
        variantId: l.variantId,
        customAttributes: [
          ...customAttributes,
          {
            key: 'jwt',
            value: localStorage.token || ''
          }
        ]
      }
    })

    const responseCheckout = await checkoutLineItemsAdd({
      checkout,
      lineItems: formattedLineItems
    })

    showToast('Added to cart')
    setCheckout(responseCheckout)
    setLoading(false)

    try {
      trackEvent({
        name: 'cartProdAdd',
        time: Date(),
        product: lineItems
      })

      window.dataLayer.push({
        event: 'addToCart',
        ecommerce: {
          currencyCode: 'CAN',
          add: {
            products: lineItems.map(li => ({
              name: li.product?.title,
              id: li.variant.sku,
              price: li.variant.price,
              brand: 'vype',
              category: '',
              variant: li.variant.title,
              quantity: li.quantity
            }))
          }
        }
      })

      window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
        quantity: e.node.quantity,
        variantId: e.node.variant.id,
        price: e.node.variant.price,
        name: e.node?.title + ' ' + e.node.variant.name
      }))
    } catch (e) {
      console.log(e)
    }

    setLoading(false)
  }

  const updateLineItemsInCart = async lineItems => {
    setLoading(true)

    const responseCheckout = await checkoutLineItemsUpdate({
      checkout,
      lineItems
    })

    setCheckout(responseCheckout)
    setCheckoutUpToDate(true)
    setLoading(false)
  }

  const updateLineItemInCart = async (lineItemId, quantity, variantId) => {
    console.log('updateLineItemInCart')
    console.log(quantity)
    console.log(variantId)
    setLoading(true)

    const lineItems = [
      {
        variantId,
        quantity
      }
    ]

    const previousQty = checkout.lineItems.edges.find(e => e.node.id === lineItemId).node.quantity

    const responseCheckout = await checkoutLineItemsUpdate({
      checkout,
      lineItems
    })

    setCheckout(responseCheckout)
    setLoading(false)
    setCheckoutUpToDate(true)

    try {
      trackEvent({
        name: quantity < previousQty ? 'cartProdRemove' : 'cartProdAdd',
        time: Date(),
        product: [
          {
            variantId,
            quantity
          }
        ]
      })

      window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
        quantity: e.node.quantity,
        variantId: e.node.variant.id,
        price: e.node.variant.price,
        name: e.node.title + ' ' + e.node.variant.name
      }))

      digitalData.push({
        cart: {
          productInfo: responseCheckout.lineItems.edges.map(e => ({
            productId: e.node.variant.id,
            productName: e.node.title + ' ' + e.node.variant.name,
            price: e.node.variant.price,
            quantity: e.node.quantity,
            attributes: {
              orderType: 'One time'
            }
          }))
        }
      })
    } catch (e) {
      console.log(e)
    }

    setLoading(false)
  }

  const removeLineItemInCart = async ({ variantId, customAttributes }) => {
    setLoading(true)
    const { title, variant } = checkout.lineItems.edges.find(l => l.node.variant.id === variantId)?.node

    const responseCheckout = await checkoutLineItemsRemove({
      checkout,
      variantId,
      customAttributes
    })

    // showToast('Item removed from cart')
    setCheckout(responseCheckout)
    setLoading(false)

    trackEvent({
      name: 'cartProdDelete',
      time: Date(),
      product: [
        {
          variantId,
          quantity: 0
        }
      ]
    })

    window.dataLayer.push({
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: 'CAN',
        remove: {
          products: [
            {
              name: title,
              id: variant?.sku,
              price: variant?.price,
              brand: 'vype',
              category: '',
              variant: variant?.title
            }
          ]
        }
      }
    })
  }

  const removeSubscriptionItemsFromCheckout = async () => {
    await Promise.mapSeries(subscriptionLineItems, async lineItem => {
      await removeLineItemInCart({
        variantId: lineItem.node.variant.id,
        customAttributes: lineItem.node.customAttributes
      })
    })
  }

  const showToast = message => {
    setToastVisible(true)
    setToastMessage(message)

    setTimeout(() => {
      setToastVisible(false)
    }, 5000)
  }

  const checkoutUrl = (urlParams = {}) => {
    let url = `/checkout`
    const hasUrlParams = Object.keys(urlParams).length > 0
    if (hasUrlParams) url += `?${Object.keys(urlParams).map(key => `${key}=${urlParams[key]}`)}`
    return url
  }

  const applyDiscountCode = async discountCode => {
    const responseCheckout = await checkoutDiscountCodeApply({
      checkout,
      discountCode
    })

    if (responseCheckout.error) {
      setApplyDiscountCodeError(responseCheckout.error)
    } else {
      setCheckout(responseCheckout)
    }
  }

  const removeDiscountCode = async () => {
    const responseCheckout = await checkoutDiscountCodeRemove({ checkout })
    setCheckout(responseCheckout)
  }

  const clearCheckout = async () => {
    await createCheckout()
  }

  const value = {
    checkout,
    loading,
    checkoutUpToDate,
    toastVisible,
    toastMessage,
    discount,
    authSdk,
    user,
    priceBreakdown,
    priceBreakdownLoading,
    tinyCartVisible,
    regularLineItems,
    subscriptionLineItems: skipSubscription ? [] : subscriptionLineItems,
    regularLineItemsQuantity,
    subscriptionLineItemsQuantity: skipSubscription ? 0 : subscriptionLineItemsQuantity,
    addLineItemsToCart,
    updateLineItemInCart,
    updateLineItemsInCart,
    removeLineItemInCart,
    createCheckout,
    checkoutUrl,
    applyDiscountCode,
    removeDiscountCode,
    setAuthSdk,
    setCheckoutUser,
    setPriceBreakdown,
    setTinyCartVisible,
    clearCheckout,
    dispatchPriceBreakdown,
    applyDiscountCodeError,
    products,
    fetchProducts,
    setSkipsubscription,
    draftOrder,
    queuedOrder,
    setQueuedOrder,
    removeSubscriptionItemsFromCheckout,
    setDraftOrder
  }

  return (
    <Context.Provider value={value}>
      <Toast visible={toastVisible} message={toastMessage} />
      {children}
    </Context.Provider>
  )
}

CheckoutProvider.propTypes = {
  children: node
}

const { Consumer } = Context
export { Consumer as CheckoutConsumer, CheckoutProvider }

export default Context
